import { useMemo, useCallback, useState } from "react";
import type { CSSProperties } from "react";
import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useFramesStore } from "@/stores/frames-store";
import {
  isFrameHiddenByMetadata,
  selectOrderedFrames,
} from "@/stores/frames-selectors";
import { useEqualSelector } from "@/hooks/use-equal-selector";
import { useReviewModeStore } from "@/stores/review-mode-store";
import {
  addFrameToFocusPresentation,
  presentFrameAt,
} from "@/lib/presentation-mode";
import { useProjectStore } from "@/stores/project-store";
import { useCanvasTimePageStore } from "@/stores/canvas-time-page-store";
import { useT } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  useTickStripLens,
  NATURAL_SLOT_H,
  STRIP_PADDING_Y,
  type LensSlot,
} from "@/hooks/use-tick-strip-lens";
import {
  agentCursorColor,
  buildPresentationGroups,
  buildTickGroups,
  tickFramesEqual,
  tickGroupsEqual,
  type TickEntry,
  type TickGroup,
} from "@/lib/frame-ticks";
import { getCanvasTimePages } from "@/lib/canvas-time-page-membership";
import { useCanvasTimePageNodes } from "@/components/canvas/use-canvas-time-page-nodes";
import { fitCanvasTimePage } from "@/lib/canvas-time-page-actions";

/**
 * Vertical ruler strip — floating ticks (no container pill).
 *
 * The active time page expands into one tick per visible frame, grouped by:
 *   1. Top: pinned frames
 *   2. Task groups: ordered by earliest frame created_at
 *   3. Unlinked: by created_at
 *
 * Every inactive time page collapses to one stacked-tick cluster marker. Hover
 * magnifies entries without reflow; clicking a tick navigates to its frame,
 * while clicking a cluster fits that page.
 *
 * When entries exceed the available rail height the slots compress so every
 * frame or cluster stays visible, and hovering applies a fisheye lens (see
 * use-tick-strip-lens.ts). The focused slot carries `data-lens-hot`, which
 * mirrors the hover treatment via group-data variants.
 */

/* ── Constants ── */
const TICK_W = 12;
const TICK_H = 2;
const NEUTRAL_TICK_COLOR = "var(--denker-ruler-tick-neutral, rgb(113 113 122))";
/** Slot height transition — 90ms at rest, 60ms while the lens is active. */
const SLOT_TRANSITION_CLASS =
  "transition-[height] duration-[90ms] ease-out [[data-lensing]_&]:duration-[60ms]";

/* ── Types ── */

interface FrameStripItem {
  key: string;
  kind: "frame";
  tick: TickEntry;
}

interface SeparatorStripItem {
  key: string;
  kind: "separator";
}

interface ClusterStripItem {
  key: string;
  kind: "cluster";
  pageIndex: number;
  label: string;
  frameCount: number;
}

type StripItem = FrameStripItem | SeparatorStripItem | ClusterStripItem;

function flattenTickGroups(groups: TickGroup[]): StripItem[] {
  const items: StripItem[] = [];
  groups.forEach((group, index) => {
    if (index > 0) {
      items.push({ key: `separator:${group.key}`, kind: "separator" });
    }
    for (const tick of group.ticks) {
      items.push({ key: `frame:${tick.nodeId}`, kind: "frame", tick });
    }
  });
  return items;
}

/* ── Component ── */

export function FrameRulerStrip() {
  const navigateToFrame = useCanvasStore((s) => s.navigateToFrame);
  const presentation = usePresentationStore((s) => s.presentation);
  const reviewUnread = useReviewModeStore((s) => s.unread);
  const reviewRecent = useReviewModeStore((s) => s.recent);
  const currentSpace = useProjectStore((s) => s.currentSpace);
  const ticketPrefix = useProjectStore(
    (s) => s.currentProject?.ticket_prefix ?? "T",
  );
  const activePageIndex = useCanvasTimePageStore(
    (state) => state.activePageIndex,
  );
  const pageNodes = useCanvasTimePageNodes();
  const t = useT(canvasMessages);
  const [tooltipItemKey, setTooltipItemKey] = useState<string | null>(null);

  const pageLayout = useCanvasTimePageStore((state) => state.layout);
  const pages = useMemo(
    () =>
      presentation
        ? []
        : getCanvasTimePages(pageNodes, currentSpace?.id ?? null, pageLayout),
    [currentSpace?.id, pageNodes, presentation, pageLayout],
  );
  const resolvedPageIndex = Math.min(
    activePageIndex,
    Math.max(0, pages.length - 1),
  );
  const activePageNodeIds = useMemo(
    () => new Set(pages[resolvedPageIndex]?.nodeIds ?? []),
    [pages, resolvedPageIndex],
  );

  // Ticks read only titles, tickets, pins and agent colours, so content
  // edits, node moves and agent cursor motion keep the previous groups.
  const tickFrames = useFramesStore(
    useEqualSelector(selectOrderedFrames, tickFramesEqual),
  );
  const metadataHiddenIds = useMemo(
    () =>
      new Set(
        tickFrames
          .filter((frame) => isFrameHiddenByMetadata(frame))
          .map((frame) => frame.frameId),
      ),
    [tickFrames],
  );
  const groups = useCanvasStore(
    useEqualSelector(
      (state) =>
        presentation
          ? buildPresentationGroups(
              presentation,
              tickFrames,
              agentCursorColor(state.agentCursors),
              [...reviewUnread, ...reviewRecent],
            )
          : buildTickGroups(
              tickFrames.filter((frame) =>
                activePageNodeIds.has(frame.frameId),
              ),
              (id) =>
                metadataHiddenIds.has(id) || state.focusHiddenFrameIds.has(id),
              agentCursorColor(state.agentCursors),
            ),
      tickGroupsEqual,
    ),
  );

  const activeFrameId = presentation
    ? (presentation.frameIds[presentation.activeIndex] ?? null)
    : null;

  const items = useMemo(() => {
    const activeItems = flattenTickGroups(groups);
    if (presentation || pages.length < 2) return activeItems;

    const pagedItems: StripItem[] = [];
    pages.forEach((page, pageIndex) => {
      if (pageIndex === resolvedPageIndex) {
        pagedItems.push(...activeItems);
      } else {
        pagedItems.push({
          key: `cluster:${page.id}`,
          kind: "cluster",
          pageIndex,
          label: page.label,
          frameCount: page.frameCount,
        });
      }
    });
    return pagedItems;
  }, [groups, pages, presentation, resolvedPageIndex]);

  const itemByKey = useMemo(
    () => new Map(items.map((item) => [item.key, item])),
    [items],
  );

  const lensSlots = useMemo<LensSlot[]>(
    () =>
      items.map((item) => ({
        isSeparator: item.kind === "separator",
        nodeId: item.kind === "separator" ? null : item.key,
      })),
    [items],
  );

  const {
    stripRef,
    registerSlot,
    compressed,
    naturalHeight,
    slotHeights,
    focusNodeIdRef,
    handleMouseMove,
    resetLens,
  } = useTickStripLens(lensSlots, setTooltipItemKey);

  const handleClick = useCallback(
    (item: FrameStripItem | ClusterStripItem) => {
      // While the lens is active the tooltip + hot styling follow the lens
      // focus, so clicks route to the focused item to match the visuals.
      const target = itemByKey.get(focusNodeIdRef.current ?? item.key) ?? item;
      if (target.kind === "cluster") {
        fitCanvasTimePage(target.pageIndex);
        return;
      }
      if (target.kind !== "frame") return;
      const targetId = target.tick.nodeId;
      const current = usePresentationStore.getState().presentation;
      if (current) {
        const index = current.frameIds.indexOf(targetId);
        if (index >= 0) presentFrameAt(index);
        else if (current.variant === "focus")
          addFrameToFocusPresentation(targetId);
        return;
      }
      navigateToFrame(targetId);
    },
    [focusNodeIdRef, itemByKey, navigateToFrame],
  );

  const handleFocusAction = useCallback((nodeId: string) => {
    const current = usePresentationStore.getState().presentation;
    if (current?.variant === "focus") {
      const index = current.frameIds.indexOf(nodeId);
      if (index >= 0) {
        presentFrameAt(index);
      } else {
        addFrameToFocusPresentation(nodeId);
      }
      return;
    }
    addFrameToFocusPresentation(nodeId);
  }, []);

  if (items.length === 0) return null;

  const slots = items.map((item, index) => {
    if (item.kind === "separator") {
      return (
        <div
          key={item.key}
          ref={(el) => registerSlot(index, el)}
          className={cn(
            "flex flex-shrink-0 items-center justify-center",
            SLOT_TRANSITION_CLASS,
          )}
          style={{ height: slotHeights[index] }}
          aria-hidden="true"
          data-testid="ruler-tick-separator"
        >
          <div
            className="bg-glass-stroke-subtle"
            style={{ width: TICK_W, height: 1 }}
          />
        </div>
      );
    }

    if (item.kind === "cluster") {
      return (
        <Button
          key={item.key}
          type="button"
          variant="ghost"
          size="iconSm"
          ref={(element) => registerSlot(index, element)}
          className={cn(
            "group relative flex flex-shrink-0 items-center justify-center p-0 focus-visible:outline-none focus-visible:ring-2",
            SLOT_TRANSITION_CLASS,
          )}
          style={{ width: TICK_W + 8, height: slotHeights[index] }}
          onClick={() => handleClick(item)}
          onMouseEnter={() => {
            if (!compressed) setTooltipItemKey(item.key);
          }}
          onMouseLeave={() => {
            if (!compressed) setTooltipItemKey(null);
          }}
          onFocus={() => setTooltipItemKey(item.key)}
          onBlur={() => setTooltipItemKey(null)}
          aria-label={t("navigateToCluster", {
            label: item.label,
            count: item.frameCount,
          })}
          data-testid={`ruler-cluster-${item.pageIndex}`}
        >
          <span
            aria-hidden="true"
            className="flex flex-col gap-px opacity-65 transition-[transform,opacity] duration-100 group-hover:scale-110 group-hover:opacity-100 group-focus-visible:scale-110 group-focus-visible:opacity-100 group-data-[lens-hot]:scale-110 group-data-[lens-hot]:opacity-100"
            style={{ width: TICK_W }}
            data-testid="ruler-cluster-glyph"
          >
            {Array.from({ length: 3 }, (_, tickIndex) => (
              <span
                key={tickIndex}
                className="denker-ruler-tick rounded-control-sm"
                style={
                  {
                    "--denker-ruler-tick-color": NEUTRAL_TICK_COLOR,
                    width: TICK_W,
                    height: TICK_H,
                  } as CSSProperties
                }
              />
            ))}
          </span>
          {tooltipItemKey === item.key ? (
            <span
              className="denker-glass-tooltip pointer-events-none absolute left-full top-1/2 z-50 ml-3 inline-flex -translate-y-1/2 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-canvas-md"
              data-testid="ruler-cluster-tooltip"
            >
              <span className="font-medium leading-none text-primary">
                {item.label}
              </span>
              <span className="text-canvas-sm font-medium text-secondary">
                {t("timePageFrames", { count: item.frameCount })}
              </span>
            </span>
          ) : null}
        </Button>
      );
    }

    const tick = item.tick;
    const isActive = activeFrameId !== null && tick.nodeId === activeFrameId;
    const focusMenuLabel = t("focusThisFrame");
    return (
      // The hit area IS the slot — clicks always match the visuals.
      <ContextMenu key={item.key}>
        <ContextMenuTrigger asChild>
          <button
            type="button"
            ref={(el) => registerSlot(index, el)}
            className={cn(
              "group relative flex flex-shrink-0 items-center justify-center",
              SLOT_TRANSITION_CLASS,
            )}
            style={{
              width: TICK_W + 8,
              height: slotHeights[index],
              padding: "0 4px",
            }}
            onClick={() => handleClick(item)}
            onMouseEnter={() => {
              if (!compressed) setTooltipItemKey(item.key);
            }}
            onMouseLeave={() => {
              if (!compressed) setTooltipItemKey(null);
            }}
            onFocus={() => setTooltipItemKey(item.key)}
            onBlur={() => setTooltipItemKey(null)}
            aria-label={t("navigateTo", { title: tick.title })}
            aria-current={isActive ? "true" : undefined}
            data-active={isActive ? "true" : undefined}
            data-testid={`ruler-tick-${tick.nodeId}`}
          >
            {/* Visual tick — thin line inside the hit area */}
            <div
              className="denker-ruler-tick rounded-control-sm opacity-40 transition-[transform,opacity,box-shadow] duration-75 ease-out group-hover:scale-y-[var(--denker-ruler-tick-active-scale)] group-hover:opacity-100 group-hover:shadow-glass-sm group-focus-visible:scale-y-[var(--denker-ruler-tick-active-scale)] group-focus-visible:opacity-100 group-data-[lens-hot]:scale-y-[var(--denker-ruler-tick-active-scale)] group-data-[lens-hot]:opacity-100 group-data-[lens-hot]:shadow-glass-sm group-data-[active]:scale-y-[var(--denker-ruler-tick-active-scale)] group-data-[active]:opacity-100 group-data-[active]:shadow-glass-sm"
              style={
                {
                  "--denker-ruler-tick-color": isActive
                    ? "var(--color-accent)"
                    : tick.color,
                  width: TICK_W,
                  height: TICK_H,
                  transformOrigin: "center",
                } as CSSProperties
              }
            />
            {tooltipItemKey === item.key && (
              <span
                className="denker-glass-tooltip pointer-events-none absolute left-full top-1/2 z-50 ml-3 inline-flex max-w-[28rem] -translate-y-1/2 items-center gap-1.5 rounded-full border px-2.5 py-1 text-canvas-md"
                data-testid="ruler-tooltip"
              >
                <span className="min-w-0 truncate whitespace-nowrap font-medium leading-none text-primary">
                  {tick.title}
                </span>
                {tick.taskTicket && (
                  <span className="shrink-0 rounded-full bg-glass-fill-heavy px-1.5 py-px text-canvas-sm font-semibold text-secondary">
                    {ticketPrefix}-{tick.taskTicket.number}
                  </span>
                )}
              </span>
            )}
          </button>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem
            icon={<Icons.Monitor />}
            onClick={() => handleFocusAction(tick.nodeId)}
            data-testid={`ruler-tick-focus-${tick.nodeId}`}
          >
            {focusMenuLabel}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    );
  });

  return (
    // Declares its natural height and lets the rail flex layout squeeze it
    // (min-h-0); the lens hook observes the squeezed size to compress slots.
    <div
      ref={stripRef}
      className="flex flex-col items-center py-1"
      style={{
        height: naturalHeight + STRIP_PADDING_Y,
        minHeight: NATURAL_SLOT_H + STRIP_PADDING_Y,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => {
        resetLens();
        setTooltipItemKey(null);
      }}
      data-testid="frame-ruler-strip"
    >
      {slots}
    </div>
  );
}
